import { API_BASE_URL } from '@synergy/config';
import { createAction } from '@reduxjs/toolkit';

import { AppDispatch } from '../../store/store';
import { ErrorTitle } from '../models/enums/error-title';
import { PoolSnackbar } from '../components/snackbars/pool-snackbar';
import { ShoppingCartApi } from '../api/shopping-cart-api';
import { ShoppingCartDto } from '../models/dto/shopping-cart-dto';
import { ShoppingCartMinimal } from '../models/shopping-cart-minimal';
import { WishListId } from '../models/wish-list-list-item';
import { enqueueSnackbar } from './snackbar-actions';

const shoppingCartApi = new ShoppingCartApi(API_BASE_URL);

export const shoppingCartReceived = createAction<ShoppingCartDto>('shoppingCart/loaded');
export const shoppingCartMinimalReceived = createAction<ShoppingCartMinimal>('shoppingCart/minimal-loaded');
export const shoppingCartInquirySent = createAction<ShoppingCartDto>('shoppingCart/inquiry-sent');
export const shoppingCartRefreshedAndSubmitted = createAction<ShoppingCartDto>('shoppingCart/refreshed-and-submitted');
export const shoppingCartEmptied = createAction('shoppingCart/emptied');
export const shoppingCartSubmitted = createAction('shoppingCart/submitted');
export const shoppingCartSingleInquirySubmitted = createAction('shoppingCart/single-inquiry-submitted');

export const fetchShoppingCart = () => async (dispatch: AppDispatch) => {
    const response = await shoppingCartApi.getShoppingCart();
    if (response.success) {
        dispatch(shoppingCartReceived(response.result));
    } else {
        dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
    }
};

export const fetchShoppingCartMinimal = () => async (dispatch: AppDispatch) => {
    const response = await shoppingCartApi.getShoppingCartMinimal();
    if (response.success) {
        dispatch(shoppingCartMinimalReceived(response.result));
    } else {
        dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
    }
};

export const removeItemsFromShoppingCart = () => async (dispatch: AppDispatch) => {
    const response = await shoppingCartApi.emptyShoppingCart();
    dispatch(shoppingCartEmptied());
    if (!response.success) {
        dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
    }
};

export const refreshAndSubmitInquiry = () => async (dispatch: AppDispatch) => {
    const response = await shoppingCartApi.refreshShoppingCart();
    if (response.success) {
        const secondResponse = await shoppingCartApi.submitInquiry();
        if (secondResponse.success) {
            dispatch(shoppingCartSingleInquirySubmitted());
            return true;
        }
        if (secondResponse.error?.title === ErrorTitle.ShoppingCartItemsChanged) {
            dispatch(fetchShoppingCart());
            return false;
        }

        dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
    } else {
        dispatch(fetchShoppingCart());
    }
    return false;
};

export const refreshAndSubmitShoppingCart = () => async (dispatch: AppDispatch) => {
    const response = await shoppingCartApi.refreshShoppingCart();
    if (response.success) {
        const secondResponse = await shoppingCartApi.submitShoppingCart();
        if (secondResponse.success) {
            dispatch(shoppingCartSubmitted());
            return true;
        }
        if (secondResponse.error?.title === ErrorTitle.ShoppingCartItemsChanged) {
            dispatch(fetchShoppingCart());
            return false;
        }

        dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
    } else {
        dispatch(fetchShoppingCart());
    }
    return false;
};

export const addCartToWishlist = (wishListId: WishListId) => async (dispatch: AppDispatch) => {
    const response = await shoppingCartApi.addToWishlist(wishListId);
    if (response.success) {
        dispatch(enqueueSnackbar(PoolSnackbar.success('app.wishList.added.all')));
    } else {
        dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
    }
};

export const updateFunctionalAsset =
    (supplierId: number, functionalAssetId: number) => async (dispatch: AppDispatch) => {
        const response = await shoppingCartApi.updateFunctionalAsset(supplierId, functionalAssetId);
        if (!response.success) {
            dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
        }
    };
